import { Role, Permission, AccessScope } from '@/types/domain/user';
import { PortalContext } from '@/config/navigation.config';

export const ROLE_PERMISSIONS_MAP: Record<Role, Permission[]> = {
  EMPLOYEE: [
    'REQUEST_VIEW',
    'REQUEST_CREATE',
    'REQUEST_EDIT',
    'REQUEST_SUBMIT',
    'REQUEST_DELETE', // Only draft requests
  ],
  MANAGER: [
    'REQUEST_VIEW',
    'REQUEST_CREATE',
    'REQUEST_EDIT',
    'REQUEST_SUBMIT',
    'REQUEST_APPROVE',
    'REQUEST_REJECT',
    'APPROVAL_VIEW',
    'APPROVAL_EXECUTE',
    'APPROVAL_DELEGATE',
    'APPROVAL_COMMENT',
    'NFA_VIEW',
    'LFA_VIEW',
    'ANALYTICS_VIEW',
  ],
  PROCUREMENT: [
    'REQUEST_VIEW',
    'NFA_VIEW',
    'NFA_CREATE',
    'LFA_VIEW',
    'LFA_CREATE',
    'PR_VIEW',
    'PR_CREATE',
    'PR_CREATE_SAP',
    'PR_SAP_SYNC',
    'PR_MANAGE',
    'RFQ_VIEW',
    'RFQ_CREATE',
    'RFQ_SEND',
    'RFQ_MANAGE',
    'VENDOR_VIEW',
    'VENDOR_MANAGE',
    'VENDOR_SCORE',
    'QUOTATION_VIEW',
    'QUOTATION_SUBMIT',
    'QUOTATION_COMPARE',
    'QUOTATION_MANAGE',
    'PO_VIEW',
    'PO_CREATE',
    'PO_MANAGE',
    'GRN_VIEW',
    'ANALYTICS_VIEW',
    'AI_RECOMMENDATIONS',
    'AI_QUERY',
  ],
  PROCUREMENT_MANAGER: [
    'REQUEST_VIEW',
    'NFA_VIEW',
    'NFA_CREATE',
    'NFA_APPROVE',
    'LFA_VIEW',
    'LFA_CREATE',
    'LFA_APPROVE',
    'PR_VIEW',
    'PR_CREATE',
    'PR_CREATE_SAP',
    'PR_SAP_SYNC',
    'PR_MANAGE',
    'RFQ_VIEW',
    'RFQ_CREATE',
    'RFQ_SEND',
    'RFQ_MANAGE',
    'VENDOR_VIEW',
    'VENDOR_MANAGE',
    'VENDOR_SCORE',
    'QUOTATION_VIEW',
    'QUOTATION_SUBMIT',
    'QUOTATION_COMPARE',
    'QUOTATION_MANAGE',
    'PO_VIEW',
    'PO_CREATE',
    'PO_APPROVE',
    'PO_MANAGE',
    'GRN_VIEW',
    'GRN_MANAGE',
    'ANALYTICS_VIEW',
    'AI_RECOMMENDATIONS',
    'AI_QUERY',
  ],
  FINANCE: [
    'REQUEST_VIEW',
    'REQUEST_APPROVE',
    'APPROVAL_VIEW',
    'APPROVAL_EXECUTE',
    'APPROVAL_DELEGATE',
    'APPROVAL_COMMENT',
    'NFA_VIEW',
    'NFA_APPROVE',
    'PO_VIEW',
    'INVOICE_VIEW',
    'INVOICE_MATCH',
    'INVOICE_MANAGE',
    'PAYMENT_VIEW',
    'PAYMENT_APPROVE',
    'PAYMENT_PROCESS',
    'PAYMENT_MANAGE',
    'ANALYTICS_VIEW',
  ],
  NFA_LFA_APPROVER: [
    'REQUEST_VIEW',
    'REQUEST_APPROVE',
    'APPROVAL_VIEW',
    'APPROVAL_EXECUTE',
    'APPROVAL_DELEGATE',
    'APPROVAL_COMMENT',
    'NFA_VIEW',
    'NFA_APPROVE',
    'LFA_VIEW',
    'LFA_APPROVE',
    'PO_VIEW',
    'ANALYTICS_VIEW',
  ],
  MANAGEMENT: [
    'REQUEST_VIEW',
    'APPROVAL_VIEW',
    'APPROVAL_EXECUTE',
    'NFA_VIEW',
    'NFA_APPROVE',
    'LFA_VIEW',
    'LFA_APPROVE',
    'PR_VIEW',
    'PO_VIEW',
    'ANALYTICS_VIEW',
    'AI_RECOMMENDATIONS',
    'AI_QUERY',
    'AUDIT_VIEW',
  ],
  SAP_INTEGRATION: [
    'PR_VIEW',
    'PR_CREATE_SAP',
    'PR_SAP_SYNC',
    'PO_VIEW',
    'GRN_VIEW',
    'INVOICE_VIEW',
    'PAYMENT_VIEW',
    'SAP_VIEW',
    'SAP_MANAGE',
    'INTEGRATION_LOGS_VIEW',
  ],
  ADMIN: [
    'REQUEST_VIEW',
    'REQUEST_CREATE',
    'REQUEST_EDIT',
    'REQUEST_SUBMIT',
    'REQUEST_APPROVE',
    'REQUEST_REJECT',
    'REQUEST_DELETE',
    'APPROVAL_VIEW',
    'APPROVAL_EXECUTE',
    'APPROVAL_DELEGATE',
    'APPROVAL_COMMENT',
    'NFA_VIEW',
    'NFA_CREATE',
    'NFA_APPROVE',
    'LFA_VIEW',
    'LFA_CREATE',
    'LFA_APPROVE',
    'PR_VIEW',
    'PR_CREATE',
    'PR_CREATE_SAP',
    'PR_SAP_SYNC',
    'PR_MANAGE',
    'RFQ_VIEW',
    'RFQ_CREATE',
    'RFQ_SEND',
    'RFQ_MANAGE',
    'VENDOR_VIEW',
    'VENDOR_MANAGE',
    'VENDOR_SCORE',
    'QUOTATION_VIEW',
    'QUOTATION_SUBMIT',
    'QUOTATION_COMPARE',
    'QUOTATION_MANAGE',
    'PO_VIEW',
    'PO_CREATE',
    'PO_APPROVE',
    'PO_MANAGE',
    'GRN_VIEW',
    'GRN_CREATE',
    'GRN_MANAGE',
    'INVOICE_VIEW',
    'INVOICE_MATCH',
    'INVOICE_MANAGE',
    'PAYMENT_VIEW',
    'PAYMENT_APPROVE',
    'PAYMENT_PROCESS',
    'PAYMENT_MANAGE',
    'ANALYTICS_VIEW',
    'AI_RECOMMENDATIONS',
    'AI_QUERY',
    'USER_VIEW',
    'USER_CREATE',
    'USER_EDIT',
    'USER_DISABLE',
    'USER_MANAGE',
    'ROLE_VIEW',
    'ROLE_MANAGE',
    'WORKFLOW_VIEW',
    'WORKFLOW_MANAGE',
    'SAP_VIEW',
    'SAP_MANAGE',
    'SAP_CONFIGURE',
    'AUDIT_VIEW',
    'INTEGRATION_LOGS_VIEW',
    'INTEGRATION_RETRY',
    'ALL',
  ],
  AUDITOR: [
    'REQUEST_VIEW',
    'APPROVAL_VIEW',
    'NFA_VIEW',
    'LFA_VIEW',
    'PR_VIEW',
    'RFQ_VIEW',
    'VENDOR_VIEW',
    'QUOTATION_VIEW',
    'PO_VIEW',
    'GRN_VIEW',
    'INVOICE_VIEW',
    'PAYMENT_VIEW',
    'ANALYTICS_VIEW',
    'AUDIT_VIEW',
    'INTEGRATION_LOGS_VIEW',
    'SAP_VIEW',
    'USER_VIEW',
    'ROLE_VIEW',
  ],
};

export const ROLE_PORTALS_MAP: Record<Role, PortalContext[]> = {
  EMPLOYEE: ['INTERNAL_PROCUREMENT', 'MAIN'],
  MANAGER: ['INTERNAL_PROCUREMENT', 'MAIN'],
  PROCUREMENT: ['MAIN', 'INTERNAL_PROCUREMENT'],
  PROCUREMENT_MANAGER: ['MAIN', 'INTERNAL_PROCUREMENT'],
  FINANCE: ['MAIN', 'INTERNAL_PROCUREMENT'],
  NFA_LFA_APPROVER: ['INTERNAL_PROCUREMENT', 'MAIN'],
  MANAGEMENT: ['MAIN', 'INTERNAL_PROCUREMENT'],
  SAP_INTEGRATION: ['SAP_SIMULATOR'],
  ADMIN: ['MAIN', 'INTERNAL_PROCUREMENT', 'ADMIN', 'SAP_SIMULATOR'],
  AUDITOR: ['MAIN', 'INTERNAL_PROCUREMENT', 'ADMIN', 'SAP_SIMULATOR'],
};

export const DEFAULT_ROLE_SCOPES: Record<Role, AccessScope> = {
  EMPLOYEE: {
    departments: ['DEP-IT'],
    plants: ['PLANT-1000'],
    businessUnits: ['BU-TECH'],
    companyCodes: ['1000'],
    ownership: 'OWN',
  },
  MANAGER: {
    departments: ['DEP-IT', 'DEP-ENG'],
    plants: ['PLANT-1000'],
    businessUnits: ['BU-TECH'],
    companyCodes: ['1000'],
    ownership: 'DEPARTMENT',
  },
  PROCUREMENT: {
    departments: ['DEP-PROC'],
    plants: ['PLANT-1000', 'PLANT-1020', 'PLANT-2000'],
    businessUnits: ['BU-GLOBAL'],
    companyCodes: ['1000'],
    ownership: 'PLANT',
  },
  PROCUREMENT_MANAGER: {
    departments: ['DEP-PROC'],
    plants: ['PLANT-1000', 'PLANT-1020', 'PLANT-1030', 'PLANT-2000', 'PLANT-3000'],
    businessUnits: ['BU-GLOBAL'],
    companyCodes: ['1000'],
    ownership: 'ENTERPRISE',
  },
  FINANCE: {
    departments: ['DEP-FIN'],
    plants: ['PLANT-1000', 'PLANT-1020', 'PLANT-1030', 'PLANT-2000', 'PLANT-3000'],
    businessUnits: ['BU-GLOBAL'],
    companyCodes: ['1000'],
    ownership: 'ENTERPRISE',
  },
  NFA_LFA_APPROVER: {
    departments: ['DEP-EXEC'],
    plants: ['PLANT-1000', 'PLANT-1020', 'PLANT-1030', 'PLANT-2000', 'PLANT-3000'],
    businessUnits: ['BU-GLOBAL'],
    companyCodes: ['1000'],
    ownership: 'ENTERPRISE',
  },
  MANAGEMENT: {
    departments: ['DEP-EXEC'],
    plants: ['PLANT-1000', 'PLANT-1020', 'PLANT-1030', 'PLANT-2000', 'PLANT-3000'],
    businessUnits: ['BU-GLOBAL'],
    companyCodes: ['1000'],
    ownership: 'ENTERPRISE',
  },
  SAP_INTEGRATION: {
    departments: ['DEP-IT'],
    plants: ['PLANT-1000', 'PLANT-2000', 'PLANT-3000'],
    businessUnits: ['BU-GLOBAL'],
    companyCodes: ['1000'],
    ownership: 'ENTERPRISE',
  },
  ADMIN: {
    departments: ['DEP-IT'],
    plants: ['PLANT-1000', 'PLANT-1020', 'PLANT-1030', 'PLANT-1040', 'PLANT-1050', 'PLANT-2000', 'PLANT-3000', 'PLANT-4000', 'PLANT-5000', 'PLANT-6000'],
    businessUnits: ['BU-GLOBAL'],
    companyCodes: ['1000'],
    ownership: 'ENTERPRISE',
  },
  AUDITOR: {
    departments: ['DEP-AUDIT'],
    plants: ['PLANT-1000', 'PLANT-1020', 'PLANT-1030', 'PLANT-1040', 'PLANT-1050', 'PLANT-2000', 'PLANT-3000', 'PLANT-4000', 'PLANT-5000', 'PLANT-6000'],
    businessUnits: ['BU-GLOBAL'],
    companyCodes: ['1000'],
    ownership: 'ENTERPRISE',
  },
};

export function hasPermissionInRoles(roles: Role[], permission: Permission): boolean {
  return roles.some((role) => {
    const perms = ROLE_PERMISSIONS_MAP[role] || [];
    return perms.includes(permission) || perms.includes('ALL');
  });
}

export function isPortalAllowedForRole(role: Role, portal: PortalContext): boolean {
  const allowedPortals = ROLE_PORTALS_MAP[role] || ['MAIN'];
  return allowedPortals.includes(portal);
}
